import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import collect from 'collect.js';
import useFeedbackHandler from '../../hooks/useFeedbackHandler';
import { DeliveryMode, OrderRejectionReason } from '../../services/exports/Constants';
import OrdersManager from '../../services/api/OrdersManager';
import ChevronLeftIcon from '../../resources/assets/icons/ChevronLeftIcon';
import { useOngoingOrders } from '../../providers/OngoingOrdersProvider';
import { Button } from 'antd';
import Drawer from '../../components/lib/Drawer';

export default function RejectOrderModal({ order }) {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:Order:RejectOrderModal' });

  const { selectedOrder, removeOrder, toggleOrderDetailsModal, toggleRejectOrderModal } = useOngoingOrders();
  const { setFeedback, renderFeedbackLabel } = useFeedbackHandler();

  const { company } = useSelector((state) => state.company);
  const [selectedReason, setSelectedReason] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!order) {
    return null;
  }

  const rejectOrder = async () => {
    setLoading(true);
    const { success, data } = await OrdersManager.reject(order.id, {
      reason: selectedReason,
    });
    setLoading(false);

    if (!success) {
      return setFeedback({
        message: data?.message,
        type: 'error',
        errors: data?.errors,
      });
    }

    toggleRejectOrderModal();

    if (selectedOrder?.id === data.data.id) {
      toggleOrderDetailsModal(null);
    }

    if (data.data.is_hidden_by_staff) {
      removeOrder(data.data.id);
    }
  };

  const reasons = collect([
    OrderRejectionReason.ItemUnavailable,
    OrderRejectionReason.ClosingSoon,
    OrderRejectionReason.StoryBusy,
    OrderRejectionReason.CustomerMistake,
  ]).when(company?.delivery_mode === DeliveryMode.External, (collection) =>
    collection.push(OrderRejectionReason.DriverNotArrived),
  );

  return (
    <Drawer open hideCloseIcon onClose={() => toggleRejectOrderModal()} zIndex={1201}>
      <div className="border-b bg-gray-50 border-gray-200 p-md flex gap-md items-center">
        <button
          className="size-8 aspect-square rounded-full bg-black flex justify-center border-none"
          onClick={() => toggleRejectOrderModal()}
        >
          <ChevronLeftIcon className="size-3 my-auto" color="white" />
        </button>
        <h6>{t('header')}</h6>
      </div>
      <div className="p-md h-full overflow-y-auto">
        <h5 className="w-full text-center">{t('title')}</h5>
        <p className="w-full text-center">{t('text')}</p>
        <p className="w-full text-center">
          {order.customer_first_name} {order.customer_last_name}
        </p>
        <p className="w-full text-center">{order.customer_phone_number}</p>
        <div className="mt-md space-y-sm">
          {reasons.map((item) => (
            <label
              htmlFor={`order-rejection-reason-${item}`}
              className="py-2 flex justify-between border-b border-gray-300 cursor-pointer"
              key={`order-rejection-reason-${item}`}
            >
              <p className="my-auto">{t(`reasons.${item}`)}</p>
              <input
                type="radio"
                checked={selectedReason === item}
                onChange={(e) => e.target.checked && setSelectedReason(item)}
                id={`order-rejection-reason-${item}`}
                className="h-4 w-4 my-auto"
              />
            </label>
          ))}
        </div>
        {renderFeedbackLabel}
      </div>
      <div className="bg-gray-50 p-md">
        <Button
          type="primary"
          danger
          disabled={!selectedReason}
          onClick={rejectOrder}
          loading={loading}
          className="w-full"
        >
          {t('buttons.confirm')}
        </Button>
      </div>
    </Drawer>
  );
}
